import { useCallback, useContext, useMemo, useState } from 'react';
import { useAuth } from '../auth/auth.hook';
import { useConfig } from '../config/config.hook';
import { configAlunoService } from './services/config-aluno.service';
import { configEducadorService } from './services/config-educador.service';
import { UsuarioContext } from './usuario.provider';
import { ConfigUsuario, Hook, PerfilAluno, Usuario } from './usuario.types';

export const useUsuario: Hook = () => {
  const { getValueOrThrow } = useConfig();
  const { token } = useAuth();
  const contexto = useContext(UsuarioContext);
  const [onboardingFinalizado, setOnboardingFinalizado] = useState<boolean>(false);

  const urlBase = useMemo(() => getValueOrThrow<string>('API_DEDICADA'), [getValueOrThrow]);

  if (!contexto) {
    throw new Error('Contexto de usuário não está pronto');
  }

  const salvarConfigUsuario = async (config: ConfigUsuario) => {
    if (config?.visualizouOnboarding) {
      setOnboardingFinalizado(true);
    }

    if (contexto.usuario.aura.perfil === 'ALUNO') {
      try {
        await configAlunoService({
          urlBase,
          token,
          idAluno: contexto.usuario.aura.dados.id,
          config,
        });
      } catch {}
    } else {
      try {
        await configEducadorService({
          urlBase,
          token,
          idEducador: contexto.usuario.aura.dados.id,
          config,
        });
      } catch {}
    }
  };

  const disciplinasCursadas = useCallback(() => {
    if (contexto.usuario.aura.perfil === 'ALUNO') {
      return (contexto.usuario as Usuario<PerfilAluno>).aura.dados.matriculas
        .flatMap((m) => m.turmasCursadas ?? [])
        .sort((a, b) => (a.periodoMatricula > b.periodoMatricula ? -1 : 1));
    }

    return [];
  }, [contexto.usuario]);

  return {
    pendente: () => contexto.pendente,
    autorizado: () => contexto.autorizado,
    perfil: () => contexto.usuario.aura.perfil,
    get: () => contexto.usuario,
    getPeriodosAnterioresAluno: () => disciplinasCursadas().flatMap((d) => d.periodoMatricula),
    getTurmasCursadasAluno: () => disciplinasCursadas(),
    problemaLogin: contexto.problemaLogin,
    config: {
      salvarConfigUsuario,
      getOnboardingFinalizado: function () {
        return onboardingFinalizado;
      },
      possuiGapDisciplinas: function () {
        return contexto.usuario.aura.dados.config.possuiGapDisciplinas;
      },
      visualizouGapDisciplinas: function () {
        return contexto.usuario.aura.dados.config.visualizouGapDisciplinas;
      },
      visualizouOnboarding: function () {
        return contexto.usuario.aura.dados.config.visualizouOnboarding;
      },
      visualizouAvisoCursosLivres: function () {
        return contexto.usuario.aura.dados.config.visualizouAvisoCursosLivres;
      },
      visualizouAvisoLabVirtual: function () {
        return contexto.usuario.aura.dados.config.visualizouAvisoLabVirtual;
      },
      visualizouAvisoAgendamentoLaboratorio: function () {
        return contexto.usuario.aura.dados.config.visualizouAvisoAgendamentoLaboratorio;
      },
      visualizouAvisoAgendamentoPraticasPresenciais: function () {
        return contexto.usuario.aura.dados.config.visualizouAvisoAgendamentoPraticasPresenciais;
      },
      getVisualizouAvisoBDQ: function () {
        return contexto.usuario.aura.dados.config.visualizouAvisoBDQ;
      },
      getTrilhaSingular: function () {
        return contexto.usuario.aura.dados.config.trilhaSingular;
      },
      getUrlComunidadeSingular: function () {
        return contexto.usuario.aura.dados.config.urlComunidadeSingular;
      },
      getSecaoSingular: function () {
        return contexto.usuario.aura.dados.config.secaoSingular ?? sessionStorage.getItem('secaoSingular');
      },
      getFaseSingular: function () {
        return contexto.usuario.aura.dados.config.faseSingular ?? sessionStorage.getItem('faseSingular');
      },
    },
  };
};
